import dayjs from '@/lib/dayjs'
import type { ConfigType, Dayjs } from 'dayjs'
import type { DiscountTargetValue } from '@/lib/enums/DiscountTarget'
import {
  DiscountTarget,
  DiscountType,
  DiscountUsagePolicy,
  type DiscountFormValues,
} from '@/lib/services/discounts/discounts.schema'
import type { Discount } from '@/lib/services/discounts/discounts.types'
import { formatCurrency, parseCurrency } from '@/lib/utils'

type DiscountValueFields = 'target' | 'type' | 'value' | 'minimum_purchase'

/** A saved discount, or the create form's values while they are typed. */
type DiscountValueLabelInput =
  | Pick<Discount, DiscountValueFields>
  | Pick<DiscountFormValues, DiscountValueFields>

/** Org-local `Y-m-d` dates, the UTC timestamps they came from, and the global list's organization. */
type DiscountDates = Pick<
  Discount,
  'starts_on' | 'ends_on' | 'starts_at' | 'ends_at' | 'organization'
>

type DiscountActivity = Pick<
  Discount,
  'usage_count' | 'has_active_reservations' | 'has_active_prepared_pos_payment_sessions'
>

export interface DiscountAvailability {
  label: 'Inactive' | 'Scheduled' | 'Expired' | 'Live'
  detail: string | null
  variant: 'secondary' | 'info' | 'muted' | 'success'
}

const discountTargetLabels = {
  [DiscountTarget.Product]: 'products',
  [DiscountTarget.Items]: 'items',
  [DiscountTarget.Delivery]: 'delivery',
  [DiscountTarget.ItemsAndDelivery]: 'items and delivery',
} satisfies Record<DiscountTargetValue, string>

function formatDiscountPercent(value: number | string): string {
  const amount = Number(value)

  if (!Number.isFinite(amount)) {
    return '0'
  }

  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 4,
  }).format(amount)
}

export function discountValueLabel(discount: DiscountValueLabelInput): string {
  const amount =
    discount.type === DiscountType.Percent
      ? `${formatDiscountPercent(discount.value)}%`
      : formatCurrency(discount.value)
  const label =
    discount.target === DiscountTarget.Delivery &&
    discount.type === DiscountType.Percent &&
    Number(discount.value) === 100
      ? 'Free delivery'
      : `${amount} off ${discountTargetLabels[discount.target]}`

  return parseCurrency(discount.minimum_purchase) > 0
    ? `${label} on purchases of ${formatCurrency(discount.minimum_purchase)} or more`
    : label
}

export function usagePolicyLabel(
  discount: Pick<Discount, 'usage_policy' | 'max_uses_per_customer'>
): string {
  if (discount.usage_policy === DiscountUsagePolicy.OneTimeCode) {
    return 'One Time Code'
  }

  if (discount.usage_policy === DiscountUsagePolicy.LimitPerCustomer) {
    return `${discount.max_uses_per_customer ?? 1} per customer`
  }

  return 'Unlimited'
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

function formatDiscountCalendarDate(value: string | null, timezone: string | undefined) {
  const parsedDate = parseDiscountCalendarDate(value, timezone)

  return parsedDate?.format('MMM D, YYYY') ?? null
}

function parseDiscountCalendarDate(
  value: string | null,
  timezone: string | undefined
): Dayjs | null {
  const normalizedValue = (value ?? '').trim()

  if (!normalizedValue) {
    return null
  }

  const parsedDate = DATE_ONLY_PATTERN.test(normalizedValue)
    ? dayjs.utc(normalizedValue, 'YYYY-MM-DD', true)
    : timezone
      ? dayjs(normalizedValue).tz(timezone)
      : dayjs(normalizedValue)

  return parsedDate.isValid() ? parsedDate : null
}

function discountCalendarDateYmd(value: string | null, timezone: string | undefined) {
  return parseDiscountCalendarDate(value, timezone)?.format('YYYY-MM-DD') ?? null
}

function prefixedDateLabel(prefix: string, value: string | null, timezone: string | undefined) {
  const formattedDate = formatDiscountCalendarDate(value, timezone)

  return formattedDate ? `${prefix} ${formattedDate}` : null
}

export function dateRangeLabel(discount: DiscountDates): string {
  const timezone = discount.organization?.timezone
  const startsAt = discount.starts_on ?? discount.starts_at
  const endsAt = discount.ends_on ?? discount.ends_at
  const formattedStartsAt = formatDiscountCalendarDate(startsAt, timezone)
  const formattedEndsAt = formatDiscountCalendarDate(endsAt, timezone)

  if (!formattedStartsAt || !formattedEndsAt) {
    return 'Always available'
  }

  return `${formattedStartsAt} - ${formattedEndsAt}`
}

export function discountAvailabilityDisplay(
  discount: Pick<Discount, 'is_active'> & DiscountDates,
  { now = dayjs() }: { now?: ConfigType } = {}
): DiscountAvailability {
  if (!discount.is_active) {
    return {
      label: 'Inactive',
      detail: 'Not available',
      variant: 'secondary',
    }
  }

  const timezone = discount.organization?.timezone
  const startsAt = discount.starts_on ?? discount.starts_at
  const endsAt = discount.ends_on ?? discount.ends_at
  const startsOn = discountCalendarDateYmd(startsAt, timezone)
  const endsOn = discountCalendarDateYmd(endsAt, timezone)
  const parsedNow = dayjs(now)
  const today = (timezone ? parsedNow.tz(timezone) : parsedNow).format('YYYY-MM-DD')

  if (startsOn && startsOn > today) {
    return {
      label: 'Scheduled',
      detail: prefixedDateLabel('Starts', startsAt, timezone),
      variant: 'info',
    }
  }

  if (endsOn && endsOn < today) {
    return {
      label: 'Expired',
      detail: prefixedDateLabel('Ended', endsAt, timezone),
      variant: 'muted',
    }
  }

  return {
    label: 'Live',
    detail:
      prefixedDateLabel('Ends', endsAt, timezone) ??
      prefixedDateLabel('Started', startsAt, timezone) ??
      'Always available',
    variant: 'success',
  }
}

export function usedAtLabel(value: string | null, timezone?: string): string {
  if (!value) {
    return 'Never'
  }

  const usedAt = dayjs(value)

  return (timezone ? usedAt.tz(timezone) : usedAt).format('MMM D, YYYY')
}

export function discountCountText(totalCount: number, isSearching: boolean): string {
  if (isSearching) {
    return `${totalCount} matching discount${totalCount === 1 ? '' : 's'}`
  }

  return `${totalCount} discount${totalCount === 1 ? '' : 's'}`
}

function hasRecordedUsage(discount: DiscountActivity) {
  return discount.usage_count > 0
}

function hasActiveReservations(discount: DiscountActivity) {
  return discount.has_active_reservations
}

function hasActivePreparedPosPaymentSessions(discount: DiscountActivity) {
  return discount.has_active_prepared_pos_payment_sessions
}

export function hasDeleteBlockingActivity(discount: DiscountActivity): boolean {
  return (
    hasRecordedUsage(discount) ||
    hasActiveReservations(discount) ||
    hasActivePreparedPosPaymentSessions(discount)
  )
}

export function deleteBlockedMessage(discount: DiscountActivity): string {
  if (hasActivePreparedPosPaymentSessions(discount)) {
    return 'Discount codes cannot be deleted while a POS card payment is prepared. Complete or cancel the prepared payment first.'
  }

  if (hasActiveReservations(discount) && !hasRecordedUsage(discount)) {
    return 'Discount codes cannot be deleted while checkout is in progress. Try again later or inactivate this code instead.'
  }

  return 'Discount codes cannot be deleted once used. Inactivate this code instead.'
}
